import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import productsData from "../data/products.json";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

function ProductListingPage() {
  const { t } = useTranslation("productlistingpage");
  const { category } = useParams();

  const [products, setProducts] = useState([]);
  const [visibleCount, setVisibleCount] = useState(12); // Number of products to show initially
  const [isLoadingMore, setIsLoadingMore] = useState(false); // Loading state for infinite scroll

  useEffect(() => {
    let isMounted = true;
    async function loadProducts() {
      if (!category) return;
      try {
        let data;
        switch (category.toLowerCase()) {
          case "rings":
            data = await import("../data/updated_products.json");
            break;
          case "earrings":
            data = await import("../data/earring_products.json");
            break;
          case "bracelets":
            data = await import("../data/bracelets_products.json");
            break;
          case "necklaces":
            data = await import("../data/necklaces_products.json");
            break;
          case "pandent":
            data = await import("../data/pandent_products.json");
            break;
          default:
            if (isMounted)
              setProducts(productsData[category.toUpperCase()] || []);
            return;
        }
        if (isMounted)
          setProducts(Array.isArray(data.default) ? data.default : []);
      } catch (error) {
        console.error(`Error loading ${category} products:`, error);
        if (isMounted) setProducts([]);
      }
    }
    loadProducts();
    setVisibleCount(12); // Reset visible count on category change
    return () => {
      isMounted = false;
    };
  }, [category]);

  // Infinite scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (visibleCount >= products.length || isLoadingMore) return; // No more products to load or already loading

      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Load more when user is 600px from bottom (before reaching footer)
      if (scrollTop + windowHeight >= documentHeight - 600) {
        setIsLoadingMore(true);
        // Simulate loading delay and then load more products
        setTimeout(() => {
          setVisibleCount((prev) => Math.min(prev + 12, products.length));
          setIsLoadingMore(false);
        }, 800); // 800ms loading delay for better UX
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [visibleCount, products.length, isLoadingMore]);

  return (
    <>
      {/* Helmet for SEO Metadata */}
      <Helmet>
        <title>{`${category} | Wellstar Jewels`}</title>
        <meta
          name="description"
          content={t("productListingPage.description", { category })}
        />
      </Helmet>

      <div className="bg-white">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 sm:px-6 sm:py-20 lg:px-8">
          {/* Page Heading */}
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6 sm:mb-8 capitalize">
            {t("productListingPage.heading", { category })}
          </h2>

          {/* Product Grid */}
          <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:gap-x-8">
            {products.length > 0 ? (
              products.slice(0, visibleCount).map((product) => (
                <div
                  key={product.id}
                  className="border border-gray-200 rounded-lg overflow-hidden shadow-md group hover:shadow-lg flex flex-col h-full"
                >
                  {/* Product Image */}
                  <Link
                    to={`/${category}/${product.id}`}
                    className="relative block"
                  >
                    <img
                      src={
                        Array.isArray(product.images)
                          ? product.images[0]
                          : product.image
                      }
                      alt={product.description || product.name}
                      className="aspect-square w-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-105"
                      loading="lazy"
                    />
                  </Link>

                  {/* Product Details */}
                  <div className="p-4 flex-1 flex flex-col justify-between">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase mb-2 truncate">
                        {t("productListingPage.materialLabel")}:{" "}
                        {product.materials?.join(", ")}
                      </p>
                      <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {t("productListingPage.nameLabel")}: {product.name}
                      </h3>
                      <p
                        className="text-sm text-gray-500 mt-2 overflow-hidden"
                        style={{ maxHeight: "3.5rem" }}
                      >
                        {t("productListingPage.descriptionLabel")}:{" "}
                        {product.description}
                      </p>
                    </div>
                    {/* optional CTA or price area could go here */}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500 col-span-full">
                {t("productListingPage.noProducts")}
              </p>
            )}
          </div>

          {/* Loading Spinner for Infinite Scroll */}
          {isLoadingMore && (
            <div className="flex justify-center items-center mt-8 py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">
                Loading more products...
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ProductListingPage;
